/**
 * Hook: Wallet balance — compute balances across all mints.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/storage/db';
import { getMintBalance, getTotalBalance } from '@/lib/storage/wallet-store';

export interface MintBalance {
  npub: string;
  name: string;
  balance: number; // piconero
}

export function useBalance() {
  // Use liveQuery to auto-update when proofs change in IndexedDB
  const proofs = useLiveQuery(() => db.walletProofs.toArray());
  const mints = useLiveQuery(() => db.walletMints.toArray());

  const [mintBalances, setMintBalances] = useState<MintBalance[]>([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!proofs || !mints) return;

    // Compute per-mint balances
    const balanceMap = new Map<string, number>();
    const nameMap = new Map<string, string>();

    for (const mint of mints) {
      nameMap.set(mint.npub, mint.name);
      balanceMap.set(mint.npub, 0);
    }

    for (const proof of proofs) {
      const current = balanceMap.get(proof.mintNpub) ?? 0;
      balanceMap.set(proof.mintNpub, current + proof.amount);
    }

    const balances: MintBalance[] = Array.from(balanceMap.entries()).map(([npub, balance]) => ({
      npub,
      name: nameMap.get(npub) ?? npub.slice(0, 8),
      balance,
    }));

    const total = balances.reduce((sum, m) => sum + m.balance, 0);

    setMintBalances(balances);
    setTotalBalance(total);
    setLoading(false);
  }, [proofs, mints]);

  return { mintBalances, totalBalance, loading };
}
